// Lógica de búsqueda de la paleta de comandos (Ctrl/⌘+K).
//
// Vive aparte del componente a propósito: es la parte que puede equivocarse en
// silencio —un ranking malo no da error, sólo hace que la gente no encuentre lo
// que busca— y sin React alrededor se puede probar de verdad.
import type { Menu } from '@/types/menu';

export const MAX_RESULTADOS = 50;

export interface Comando {
  ruta: string; nombre: string; menu: string; submenu: string; icon: string | null;
  nNombre: string; palabras: string[];
  palabrasUbic: string[];   // palabras del módulo y el submenú
  nUbicacion: string;       // submenú + módulo + ruta
  nDescripcion: string;
}

const partirPalabras = (s: string) => s.split(/[\s/·—–\-(),.:;]+/).filter(Boolean);

/** Sin tildes y en minúsculas: quien busca «nomina» debe encontrar «Nómina». */
export const norm = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function aplanar(menus: Menu[] | undefined): Comando[] {
  const out: Comando[] = [];
  for (const m of menus || []) {
    for (const s of m.submenus || []) {
      for (const o of s.opciones || []) {
        out.push({
          ruta: o.ruta, nombre: o.nombre, menu: m.nombre, submenu: s.nombre, icon: o.icon,
          nNombre: norm(o.nombre),
          palabras: partirPalabras(norm(o.nombre)),
          palabrasUbic: partirPalabras(norm(`${m.nombre} ${s.nombre}`)),
          nUbicacion: norm(`${s.nombre} ${m.nombre} ${o.ruta}`),
          nDescripcion: norm(o.descripcion || ''),
        });
      }
    }
  }
  return out;
}

/**
 * Cuanto MENOR el número, mejor la coincidencia. −1 = no coincide.
 *
 * El nivel 2 —una palabra del MÓDULO o del SUBMENÚ empieza por el término— va
 * deliberadamente por delante del 3, que es una coincidencia a mitad de palabra
 * dentro del nombre. Sin eso, escribir «nomina» colocaba «Denominaciones» por
 * encima de las opciones del propio módulo Nómina, que es justo lo contrario de
 * lo que quería quien escribió eso.
 *
 * El nivel 5 (la descripción de la opción) es el que da alcance: las 394
 * opciones tienen descripción, así que «impuesto» encuentra la Conciliación
 * Tributaria aunque esa palabra no esté en su nombre. Va última porque es la
 * señal más débil y la que más ruido generaría arriba.
 */
export function puntuar(c: Comando, t: string): number {
  if (c.nNombre.startsWith(t)) return 0;
  if (c.palabras.some((p) => p.startsWith(t))) return 1;
  if (c.palabrasUbic.some((p) => p.startsWith(t))) return 2;
  if (c.nNombre.includes(t)) return 3;
  if (c.nUbicacion.includes(t)) return 4;
  if (c.nDescripcion.includes(t)) return 5;
  return -1;
}

/**
 * Todos los términos deben casar (búsqueda AND) y el orden en que se escriben no
 * importa: «cont asien» encuentra Contabilidad › Asientos igual que «asien cont».
 */
export function buscar(cmds: Comando[], q: string): Comando[] {
  const terminos = norm(q).split(/\s+/).filter(Boolean);
  if (!terminos.length) return [];
  const hits: { c: Comando; score: number }[] = [];
  for (const c of cmds) {
    let peor = 0; let casa = true;
    for (const t of terminos) {
      const s = puntuar(c, t);
      if (s < 0) { casa = false; break; }
      peor = Math.max(peor, s);
    }
    if (casa) hits.push({ c, score: peor });
  }
  hits.sort((a, b) =>
    a.score - b.score ||
    a.c.nombre.length - b.c.nombre.length ||
    a.c.nombre.localeCompare(b.c.nombre, 'es'));
  return hits.slice(0, MAX_RESULTADOS).map((h) => h.c);
}
